import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import AiLock from "@/components/AiLock";
import AiExpiryBanner from "@/components/AiExpiryBanner";
import { useAuth } from "@/context/AuthContext";
import { useLocation, Link } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { Send, Sparkles, MessageSquareText, BookOpen, X, ArrowLeft, BookmarkPlus, Check } from "lucide-react";

const COACH_ORB = "https://static.prod-images.emergentagent.com/jobs/ce1cbc9d-1dad-4cd9-98ed-a62f3c8b6634/images/00e9cc87cfcd391f7f51f7ad52b7877c51580df65be6666c0e2393a9512bb1e4.png";

const SUGGESTIONS = [
  "Erklär mir den Unterschied zwischen Lux und Lumen einfach.",
  "Welche Lichtfarbe und Lux-Werte empfiehlst du für ein Boutique-Café?",
  "Wann brauche ich einen Wallwasher statt Downlights?",
  "Prüfe mein Konzept: offenes Büro, 4 m Decke, viel Glas, Bildschirmarbeit.",
];

export default function Coach() {
  const { user } = useAuth();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [usage, setUsage] = useState(null);
  const [expired, setExpired] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [savedNotes, setSavedNotes] = useState(new Set());
  const scrollRef = useRef(null);

  const aiActive = user && (user.role === "admin" || user.ai_active) && !expired;

  const loadUsage = () => api.get("/me/usage").then((r) => setUsage(r.data.coach)).catch((e) => console.error("Usage konnte nicht geladen werden:", e));

  useEffect(() => {
    api.get("/coach/history").then((r) => setMessages(r.data.messages || [])).catch((e) => console.error("Coach-Verlauf konnte nicht geladen werden:", e));
    loadUsage();
  }, []);

  useEffect(() => {
    const st = location.state;
    if (st?.moduleId && st?.lessonId) {
      setCtx({ module_id: st.moduleId, lesson_id: st.lessonId, label: `Modul ${st.moduleNumber} · ${st.lessonTitle}` });
      return;
    }
    let active = true;
    (async () => {
      try {
        const [pr, co] = await Promise.all([api.get("/progress"), api.get("/course")]);
        const lv = pr.data?.last_viewed;
        if (!lv || !active) return;
        const mods = co.data?.modules || [];
        const mod = mods.find((mm) => mm.id === lv.module_id);
        const les = mod?.lessons?.find((ll) => ll.id === lv.lesson_id);
        if (mod && les && active) setCtx({ module_id: lv.module_id, lesson_id: lv.lesson_id, label: `Modul ${mod.number} · ${les.title}` });
      } catch (e) { /* kein Kontext */ }
    })();
    return () => { active = false; };
  }, [location.state]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("lux_token");
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg, ...(ctx ? { module_id: ctx.module_id, lesson_id: ctx.lesson_id } : {}) }),
      });
      if (!res.ok || !res.body) {
        let detail = "Entschuldigung, es gab ein Verbindungsproblem. Bitte versuchen Sie es erneut.";
        try {
          const data = await res.json();
          if (data?.detail) detail = data.detail;
        } catch (_) {}
        if (res.status === 429) toast.error(detail);
        if (res.status === 403) { setExpired(true); toast.error(detail); }
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: detail };
          return copy;
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      console.error("Coach-Stream fehlgeschlagen:", e);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Entschuldigung, es gab ein Verbindungsproblem. Bitte versuchen Sie es erneut." };
        return copy;
      });
    } finally {
      setStreaming(false);
      loadUsage();
    }
  };

  const saveAsNote = async (i) => {
    if (!ctx) return;
    const answer = messages[i]?.content || "";
    const prev = messages[i - 1];
    const question = prev?.role === "user" ? prev.content : "";
    const text = question ? `Frage: ${question}\n\nCoach: ${answer}` : `Coach: ${answer}`;
    try {
      await api.post(`/lesson-meta/${ctx.module_id}/${ctx.lesson_id}/append-note`, { text });
      setSavedNotes((s) => new Set(s).add(i));
      toast.success("Als Notiz zur Lektion gespeichert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Konnte nicht gespeichert werden.");
    }
  };

  const empty = messages.length === 0;

  if (!aiActive) {
    return (
      <div className="min-h-screen bg-background grain flex flex-col">
        <Navbar />
        <AiLock feature="den KI-Coach" onUnlocked={() => setExpired(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grain flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 py-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>
        <AiExpiryBanner />
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative h-12 w-12 rounded-full overflow-hidden flex-shrink-0">
            <img src={COACH_ORB} alt="Coach" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-xl flex items-center gap-2">LUX Licht-Coach <Sparkles className="h-4 w-4 text-gold" /></h1>
            <p className="text-xs text-muted-foreground">
              Ihr persönlicher Lichtplanungs-Mentor · powered by Lux Solutions
              {usage && usage.remaining !== null && (
                <span data-testid="coach-usage" className="ml-2 text-gold/90">· Heute noch {usage.remaining}/{usage.limit} Fragen</span>
              )}
              {user && user.ai_days_left != null && (
                <span data-testid="coach-ai-days" className="ml-2 text-foreground/50">· KI-Zugang: noch {user.ai_days_left} Tage</span>
              )}
              {user?.role === "trial" && (
                <span data-testid="coach-trial" className="ml-2 text-emerald-300/90">· Test-Zugang</span>
              )}
            </p>
          </div>
        </div>

        {ctx && (
          <div data-testid="coach-context" className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/10 border border-gold/25 text-gold text-xs">
              <BookOpen className="h-3 w-3" /> Bezug: {ctx.label}
              <button onClick={() => setCtx(null)} data-testid="coach-context-clear" aria-label="Kontext entfernen" className="ml-1 hover:text-foreground transition-colors">
                <X className="h-3 w-3" />
              </button>
            </span>
            <Link
              to={`/module/${ctx.module_id}/lesson/${ctx.lesson_id}`}
              data-testid="coach-back-to-lesson"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass hover:glow-gold transition-all duration-300 text-xs text-foreground/80"
            >
              <ArrowLeft className="h-3 w-3 text-gold" /> Zurück zur Lektion
            </Link>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto rounded-2xl glass p-4 sm:p-6 space-y-4">
          {empty && (
            <div className="h-full flex flex-col items-center justify-center text-center py-10">
              <div className="relative h-24 w-24 rounded-full overflow-hidden mb-5">
                <img src={COACH_ORB} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 blur-xl bg-gold/30 -z-10 animate-pulse-glow" />
              </div>
              <h2 className="font-heading font-bold text-2xl">Fragen Sie mich alles über Licht.</h2>
              <p className="text-muted-foreground mt-2 max-w-md">Von Photometrie über Normen bis zur Konzeptprüfung – ich übersetze Technik in Wirkung.</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-7 w-full max-w-xl">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`coach-suggestion-${i}`}
                    className="text-left text-sm rounded-xl glass p-3.5 hover:glow-gold transition-all duration-300 text-foreground/80"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => {
            const isAssistant = m.role === "assistant";
            const isLast = i === messages.length - 1;
            const showSave = isAssistant && ctx && m.content && !(streaming && isLast);
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}
              >
                <div
                  data-testid={`coach-message-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-white/10 border border-white/10 rounded-br-sm"
                      : "bg-gold/10 border border-gold/20 rounded-bl-sm"
                  }`}
                >
                  {m.content || (streaming && isLast ? <span className="inline-flex gap-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span> : "")}
                </div>
                {showSave && (
                  <button
                    onClick={() => saveAsNote(i)}
                    disabled={savedNotes.has(i)}
                    data-testid={`coach-save-note-${i}`}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-gold transition-colors disabled:text-gold/90 disabled:cursor-default"
                  >
                    {savedNotes.has(i)
                      ? (<><Check className="h-3 w-3" /> Als Notiz gespeichert</>)
                      : (<><BookmarkPlus className="h-3 w-3" /> Als Notiz zur Lektion speichern</>)}
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Input */}
        <div className="mt-4 flex items-end gap-3">
          <div className="flex-1 flex items-center gap-3 rounded-2xl glass px-4 py-3 focus-within:border-gold/40 transition-all">
            <MessageSquareText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Stellen Sie dem Coach eine Frage…"
              data-testid="coach-input"
              className="flex-1 bg-transparent outline-none resize-none text-foreground placeholder:text-muted-foreground/60 max-h-32"
            />
          </div>
          <button
            onClick={() => send()}
            disabled={streaming || !input.trim()}
            data-testid="coach-send"
            className="h-12 w-12 flex-shrink-0 rounded-2xl bg-gold text-[#0a0d12] flex items-center justify-center hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-50"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </main>
    </div>
  );
}

function Dot({ d = 0 }) {
  return <span className="h-2 w-2 rounded-full bg-gold/70 inline-block animate-pulse-glow" style={{ animationDelay: `${d}s` }} />;
}
